import { useState, useEffect, useMemo } from 'react'
import { AlertCircle, HelpCircle, Sparkles, Loader2, X, Plus } from '@/ui/icons'
import { Button } from '@/ui/design-system'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/ui/components/primitives/tooltip'
import { GENERATE_INGREDIENTS_TOOLTIP_COPY } from '@/shared/copy/tooltips'
import { Input, Textarea, Label } from '@/ui/components'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/ui/components'
import { CreateMealNeedRequest, UpdateMealNeedRequest, MealNeed } from '@/shared/api/mealNeeds'
import { useAuth } from '@/modules/auth'
import { useSubscription } from '@/modules/subscriptions/hooks/useSubscription'
import { RangerApi } from '@/shared/api/ranger'
import { ApiError } from '@/shared/api/types'
import { useTripMembersDietaryRestrictions } from '../hooks/useTripMembersDietaryRestrictions'
import { getDietaryRestrictionLabel, DietaryRestrictionType } from '@/shared/types'
import { cn } from '@/shared/utils'

interface AddMealFormProps {
  onSave: (data: CreateMealNeedRequest | UpdateMealNeedRequest) => Promise<void>
  onCancel: () => void
  isLoading: boolean
  editingMeal?: MealNeed | null
  tripId: string
  tripStartDate?: string | null
  tripEndDate?: string | null
  compact?: boolean
}

export function AddMealForm({
  onSave,
  onCancel,
  isLoading,
  editingMeal,
  tripId,
  tripStartDate,
  tripEndDate,
  compact = false
}: AddMealFormProps) {
  const [formData, setFormData] = useState<{
    name: string
    description: string
    meal_type: string
    planned_date: string
    planned_meal_time: 'breakfast' | 'lunch' | 'dinner' | 'snack'
    ingredients: string[]
    serves_count?: number
  }>({
    name: '',
    description: '',
    meal_type: 'dinner',
    planned_date: tripStartDate || new Date().toISOString().split('T')[0],
    planned_meal_time: 'dinner',
    ingredients: [],
    serves_count: undefined
  })

  const [newIngredient, setNewIngredient] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isGeneratingIngredients, setIsGeneratingIngredients] = useState(false)
  const [ingredientGenerationError, setIngredientGenerationError] = useState<string | null>(null)

  const { getAccessToken } = useAuth()
  const { canUseRanger } = useSubscription()
  const rangerApi = useMemo(() => new RangerApi(getAccessToken), [getAccessToken])

  const { allProfiles } = useTripMembersDietaryRestrictions(tripId)

  // Deduplicate crew allergy + dietary labels for the banner
  const uniqueRestrictionLabels = useMemo(() => {
    const seen = new Set<string>()
    const labels: string[] = []
    const push = (label: string) => {
      const key = label.toLowerCase()
      if (label && !seen.has(key)) {
        seen.add(key)
        labels.push(label)
      }
    }
    for (const profile of allProfiles) {
      for (const allergy of profile.allergies) {
        push(allergy.charAt(0).toUpperCase() + allergy.slice(1))
      }
      for (const diet of profile.dietaryRestrictions) {
        const label = getDietaryRestrictionLabel(diet as DietaryRestrictionType)
        push(label !== 'Unknown' ? label : diet.charAt(0).toUpperCase() + diet.slice(1))
      }
    }
    return labels
  }, [allProfiles])

  const getDefaultDate = useMemo(() => {
    const today = new Date().toISOString().split('T')[0]
    if (tripStartDate) {
      if (tripStartDate >= today) return tripStartDate
      if (tripEndDate && today <= tripEndDate) return today
      return tripStartDate
    }
    return today
  }, [tripStartDate, tripEndDate])

  useEffect(() => {
    if (editingMeal) {
      setFormData({
        name: editingMeal.name || '',
        description: editingMeal.description || '',
        meal_type: editingMeal.meal_type || 'dinner',
        planned_date: editingMeal.planned_date || getDefaultDate,
        planned_meal_time: editingMeal.planned_meal_time || 'dinner',
        ingredients: editingMeal.ingredients || [],
        serves_count: editingMeal.serves_count || undefined
      })
    } else {
      setFormData({
        name: '',
        description: '',
        meal_type: 'dinner',
        planned_date: getDefaultDate,
        planned_meal_time: 'dinner',
        ingredients: [],
        serves_count: undefined
      })
    }
    setNewIngredient('')
    setErrors({})
    setIngredientGenerationError(null)
  }, [editingMeal, getDefaultDate])

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
    if (field === 'planned_date' && value) {
      const dateErrors: Record<string, string> = {}
      if (tripStartDate && value < tripStartDate) {
        dateErrors.planned_date = `Date cannot be before trip start (${tripStartDate})`
      }
      if (tripEndDate && value > tripEndDate) {
        dateErrors.planned_date = `Date cannot be after trip end (${tripEndDate})`
      }
      if (Object.keys(dateErrors).length > 0) {
        setErrors(prev => ({ ...prev, ...dateErrors }))
      }
    }
  }

  const handleNumberChange = (field: string, value: string) => {
    const numValue = value === '' ? undefined : parseInt(value, 10)
    if (value === '' || (!isNaN(numValue!) && numValue! >= 0)) {
      const finalValue = value === '' || numValue === 0 ? undefined : numValue
      setFormData(prev => ({ ...prev, [field]: finalValue }))
    }
  }

  const addIngredient = () => {
    if (newIngredient.trim()) {
      setFormData(prev => ({ ...prev, ingredients: [...prev.ingredients, newIngredient.trim()] }))
      setNewIngredient('')
    }
  }

  const removeIngredient = (index: number) => {
    setFormData(prev => ({ ...prev, ingredients: prev.ingredients.filter((_, i) => i !== index) }))
  }

  const handleGenerateIngredients = async () => {
    setIngredientGenerationError(null)
    if (!formData.name.trim()) {
      setIngredientGenerationError('Please enter a meal name first to generate ingredients.')
      return
    }
    if (!canUseRanger) {
      return
    }
    setIsGeneratingIngredients(true)
    try {
      const response = await rangerApi.generateIngredients({
        mealName: formData.name.trim(),
        tripId,
        mealType: formData.meal_type,
        servingSize: formData.serves_count || undefined
      })
      if (response.ingredients && response.ingredients.length > 0) {
        const existing = new Set(formData.ingredients.map(i => i.toLowerCase()))
        const newOnes = response.ingredients.filter(ing => !existing.has(ing.toLowerCase()))
        setFormData(prev => ({ ...prev, ingredients: [...prev.ingredients, ...newOnes] }))
      } else {
        setIngredientGenerationError('No ingredients were generated. Try again or add manually.')
      }
    } catch (error) {
      let errorMessage = 'Failed to generate ingredients. Please try again.'
      if (error instanceof ApiError) {
        if (error.status === 403) {
          errorMessage = 'Ranger AI requires a Pro subscription.'
        } else {
          errorMessage = error.message || errorMessage
        }
      }
      setIngredientGenerationError(errorMessage)
    } finally {
      setIsGeneratingIngredients(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: Record<string, string> = {}
    if (!formData.name.trim()) {
      newErrors.name = 'Meal name is required'
    }
    if (tripStartDate && formData.planned_date < tripStartDate) {
      newErrors.planned_date = `Date cannot be before trip start (${tripStartDate})`
    }
    if (tripEndDate && formData.planned_date > tripEndDate) {
      newErrors.planned_date = `Date cannot be after trip end (${tripEndDate})`
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    const basePayload: CreateMealNeedRequest = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      meal_type: formData.meal_type,
      planned_date: formData.planned_date,
      planned_meal_time: formData.planned_meal_time,
      ingredients: formData.ingredients,
      is_collaborative: false,
      cooking_equipment: [],
      dietary_restrictions: [],
      allergens: []
    }

    if (editingMeal) {
      const updatePayload: UpdateMealNeedRequest = { ...basePayload }
      if (formData.serves_count !== undefined && formData.serves_count > 0) {
        updatePayload.serves_count = formData.serves_count
      } else if (editingMeal.serves_count !== undefined && editingMeal.serves_count !== null) {
        // User cleared serves_count — send null to remove (cast needed)
        ;(updatePayload as any).serves_count = null
      }
      await onSave(updatePayload)
    } else {
      if (formData.serves_count !== undefined && formData.serves_count > 0) {
        basePayload.serves_count = formData.serves_count
      }
      await onSave(basePayload)
    }
  }

  const spacing = compact ? 'space-y-3' : 'space-y-4'

  return (
    <form onSubmit={handleSubmit} className={spacing}>
      {/* Crew dietary restrictions banner */}
      {uniqueRestrictionLabels.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800 dark:text-amber-300">
              <span className="font-medium">Crew dietary needs:</span>{' '}
              {uniqueRestrictionLabels.join(', ')}
            </div>
          </div>
        </div>
      )}

      {/* Meal name */}
      <div className="space-y-1.5">
        <Label htmlFor="meal-name" className="text-xs font-medium text-earth-500">
          Meal Name *
        </Label>
        <Input
          id="meal-name"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          placeholder="e.g., Grilled Chicken Tacos"
          disabled={isLoading}
          className={cn(errors.name && 'border-destructive')}
        />
        {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
      </div>

      {/* Description */}
      <div className="space-y-1.5">
        <Label htmlFor="meal-description" className="text-xs font-medium text-earth-500">
          Description
        </Label>
        <Textarea
          id="meal-description"
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Describe the meal..."
          rows={compact ? 2 : 3}
          disabled={isLoading}
          className="resize-none"
        />
      </div>

      {/* Date + Meal Time row */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="meal-date" className="text-xs font-medium text-earth-500">
            Date *
          </Label>
          <Input
            id="meal-date"
            type="date"
            value={formData.planned_date}
            onChange={(e) => handleChange('planned_date', e.target.value)}
            min={tripStartDate || undefined}
            max={tripEndDate || undefined}
            disabled={isLoading}
            className={cn(errors.planned_date && 'border-destructive')}
          />
          {errors.planned_date && (
            <p className="text-xs text-destructive">{errors.planned_date}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="meal-time" className="text-xs font-medium text-earth-500">
            Meal Time
          </Label>
          <Select
            value={formData.planned_meal_time}
            onValueChange={(v) => handleChange('planned_meal_time', v)}
            disabled={isLoading}
          >
            <SelectTrigger id="meal-time">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="breakfast">Breakfast</SelectItem>
              <SelectItem value="lunch">Lunch</SelectItem>
              <SelectItem value="dinner">Dinner</SelectItem>
              <SelectItem value="snack">Snack</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Serves count */}
      <div className="space-y-1.5">
        <Label htmlFor="meal-serves" className="text-xs font-medium text-earth-500">
          Serves
        </Label>
        <Input
          id="meal-serves"
          type="number"
          value={formData.serves_count || ''}
          onChange={(e) => handleNumberChange('serves_count', e.target.value)}
          placeholder="e.g., 4"
          min="0"
          disabled={isLoading}
        />
      </div>

      {/* Ingredients */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs font-medium text-earth-500">Ingredients</Label>
          {canUseRanger && (
            <div className="flex items-center gap-1">
              <Button
                type="button"
                onClick={handleGenerateIngredients}
                disabled={!formData.name.trim() || isLoading || isGeneratingIngredients}
                variant="outline"
                size="sm"
                className="text-xs h-7 px-2 text-earth-700 dark:text-foreground"
              >
                {isGeneratingIngredients ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3 h-3 mr-1" />
                    Generate
                  </>
                )}
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="How does generate ingredients work?"
                  >
                    <HelpCircle className="w-3.5 h-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="end" className="max-w-xs">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {GENERATE_INGREDIENTS_TOOLTIP_COPY}
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>

        {ingredientGenerationError && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-2 flex items-start gap-2">
            <AlertCircle className="w-3.5 h-3.5 text-destructive flex-shrink-0 mt-0.5" />
            <p className="text-xs text-destructive flex-1">{ingredientGenerationError}</p>
            <button
              type="button"
              onClick={() => setIngredientGenerationError(null)}
              className="text-destructive/70 hover:text-destructive"
              aria-label="Dismiss"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        {formData.ingredients.length > 0 && (
          <div className="rounded-md border border-earth-200 dark:border-earth-600 dark:text-foreground p-2">
            <ul className="space-y-1">
              {formData.ingredients.map((ingredient, index) => (
                <li key={index} className="flex items-center justify-between text-xs text-earth-700 dark:text-foreground">
                  <span className="flex-1">• {ingredient}</span>
                  <button
                    type="button"
                    onClick={() => removeIngredient(index)}
                    className="ml-2 text-earth-400 hover:text-destructive transition-colors"
                    disabled={isLoading}
                    aria-label={`Remove ${ingredient}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex gap-2">
          <Input
            value={newIngredient}
            onChange={(e) => setNewIngredient(e.target.value)}
            placeholder="e.g., Chicken, Tortillas"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addIngredient()
              }
            }}
            disabled={isLoading}
            className="text-sm"
          />
          <Button
            type="button"
            onClick={addIngredient}
            disabled={!newIngredient.trim() || isLoading}
            variant="outline"
            size="sm"
            className="shrink-0"
          >
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading} className="gap-2">
          {isLoading ? (
            <>
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              {editingMeal ? 'Saving...' : 'Adding...'}
            </>
          ) : (
            editingMeal ? 'Save Changes' : 'Add Meal'
          )}
        </Button>
      </div>
    </form>
  )
}
